import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { StorageService, StorageKey } from '../services/storage.service';
// Phase CS-E — PosStateService lives under features/pos. Pulling it in
// via the interceptor creates a soft core → features cross-import, but
// Angular's DI handles it cleanly and every existing route chain already
// resolves both in the same injector (standalone app bootstrap). Kept
// as a lazy Injector resolution below so this file does not hard-depend
// on a feature module being eager-loaded.
import { PosStateService } from '../../features/pos/pos-state.service';
import { NetworkStatusService } from '../offline/network-status.service';
import { HubDeviceService } from '../offline/hub-device.service';
import { HubDiscoveryService } from '../offline/hub-discovery.service';
import { hubCanTakeOverHere } from '../offline/hub-surfaces';

/**
 * Global error interceptor for handling authentication failures.
 *
 * Responsibilities:
 * - Detects 401 (Unauthorized) and 402 (Payment Required - used as custom auth error) responses
 * - Automatically clears expired/invalid tokens from storage
 * - Redirects users to appropriate login screens (POS PIN or admin login)
 * - Prevents users from staying in "broken authenticated" state
 * - OFP-082: reports a transport-level failure (status 0) on an /api/ call
 *   to NetworkStatusService, saying whether a hub could take over here
 *
 * Flow:
 * 1. HTTP request fails with 401/402
 * 2. Interceptor catches the error
 * 3. Determines route type (POS vs admin)
 * 4. Clears appropriate session tokens
 * 5. Redirects to login screen
 * 6. Re-throws error for component-level handling
 * 
 * Why this is needed:
 * - Backend validates JWT signatures and expiration
 * - Frontend may have expired token in localStorage
 * - Without this, UI appears "logged in" but all API calls fail
 * - This provides automatic recovery from expired sessions
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const storage = inject(StorageService);
  const injector = inject(Injector);
  const net = inject(NetworkStatusService);

  return next(req).pipe(
    catchError((error: unknown) => {
      // Only handle HTTP errors
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      // OFP-082 — status 0: no response at all. Tell the reachability logic
      // rather than waiting for the next 10 s heartbeat. Where a hub can
      // take over, it starts a confirmation run (two probes 3 s apart,
      // ~6–7 s to fail over instead of up to ~19 s); everywhere else it
      // probes once, as LB-8's report always did, because a false "offline"
      // there buys nothing (and costs RECON-18's short recovery). Before
      // this, only the hub device's LB-8 retry ever reported a failure. A
      // report never flips state by itself — the probes decide. API calls
      // only: a failed asset or translation fetch says nothing about the API.
      if (error.status === 0 && req.url.includes('/api/')) {
        try {
          net.reportFailure({ hubCanTakeOver: hubCanTakeOverHere(injector) });
          // OFP-079 re-review — the hub device's own hub did not answer a
          // request: have its probe look now rather than on its 30 s cadence.
          const hubDevice = injector.get(HubDeviceService, null);
          if (hubDevice?.isHubDevice()) {
            const hub = injector.get(HubDiscoveryService, null);
            const base = hub?.effectiveUrl()?.replace(/\/+$/, '');
            if (hub && base && req.url.startsWith(base)) hub.nudge();
          }
        } catch {
          // Never let the report swallow the error the caller is waiting for.
        }
      }

      // Phase CS-E — structured 409 session_closed safety net. Covers the
      // missed-WebSocket case (AppSync hiccup, offline terminal, etc.):
      // even if the realtime session.closed event never reached the
      // shell, the very next POS write will come back 409 with
      // { code: "session_closed" } (CS-B), and we use that to bounce the
      // operator to the PIN screen with the "Cashier session ended"
      // context — identical UX to the realtime path. Errors continue to
      // propagate to component handlers so any inline error UI still runs.
      if (error.status === 409) {
        const body: any = error.error;
        if (body && typeof body === 'object' && body.code === 'session_closed') {
          try {
            const posState = injector.get(PosStateService, null);
            if (posState) {
              const sessionId = typeof body.sessionId === 'string' ? body.sessionId : null;
              posState.lockScreenForSessionClose(sessionId);
            }
          } catch {
            // Defensive: never let the interceptor swallow an error just
            // because PosStateService couldn't be resolved. The
            // component-level handler will still surface a toast.
          }
        }
      }

      // ✅ Handle authentication errors globally (401/402)
      if (error.status === 401 || error.status === 402) {
        // Skip interceptor for auth/approval/signup endpoints - let components handle those errors
        const url = error.url || '';
        const isAuthRequest = url.includes('/auth/login') || 
                              url.includes('/auth/pin-login') ||
                              url.includes('/auth/forgot-password') ||
                              url.includes('/auth/reset-password') ||
                              url.includes('/auth/verify-email') ||
                              url.includes('/pos/approvals/authorize') ||
                              url.includes('/trial-signup');
        
        if (isAuthRequest) {
          // Let the component handle the error and display the message
          return throwError(() => error);
        }

        // POS bootstrap reads (offline flag + currency self-heal) must
        // NOT tear down the session if the server momentarily 401s. The
        // cashier just signed in via PIN; bouncing them back to /login
        // on a stale token race produces an infinite redirect loop. The
        // matching backend endpoints already grant POS roles; this is a
        // belt-and-braces guard for any future regression in token routing.
        const isStoreSettingsRead =
          /\/api\/stores\/[0-9a-fA-F-]+\/settings(\/currency)?(\?|$)/.test(url);
        if (isStoreSettingsRead) {
          return throwError(() => error);
        }

        // CFD-1 — the Customer Facing Display is a public storeCode-credential
        // surface with no tenant/POS session. A 401 on its trimmed check-read
        // (expired cfdToken) must NOT fall through to the admin-session
        // teardown + /login redirect below — that would navigate the rear
        // guest screen to the operator login. The display shell handles the
        // error itself (re-launch on next pointer), so just surface it.
        const isCustomerDisplayRead =
          /\/api\/public\/store\/[^/]+\/customer-display\/check\//.test(url);
        if (isCustomerDisplayRead) {
          return throwError(() => error);
        }

        // CALLERID-H1 — the caller-ID inbox is a background poll nobody
        // clicked. Its 401 says the cloud cannot verify this token (say a
        // hub-minted one) — not that the cashier should be signed out of a
        // hub that may still be serving them. The poll backs off by itself.
        const isCallerInboxRead = /\/api\/pos\/caller-id(\?|$)/.test(url);
        if (isCallerInboxRead) {
          return throwError(() => error);
        }

        console.warn('[ErrorInterceptor] Authentication error detected', {
          status: error.status,
          url: error.url,
          message: error.message
        });
        
        // POS context is determined by the URL path OR by a persisted
        // standalone store code with no admin session present. The storage
        // check matters because an auth failure can fire during cold-start
        // bootstrap before the router has settled the URL — on a dedicated
        // edge-client terminal we must still recover to the POS PIN screen,
        // never the admin login.
        const standaloneCode = storage.getRaw(StorageKey.POS_STANDALONE_CODE);
        // OFFLINE-AUTH-401 (2026-08-27) — decide from the TOKEN that failed,
        // not from where the browser happens to be.
        //
        // Reported as: after an outage, syncing sometimes logs the operator
        // out to the ADMIN warning screen. The path rule below is
        // timing-dependent (a 401 can fire before the router settles) and
        // `isPosOnlyDevice` is defeated by a stale admin token that someone
        // signed in with months ago — so a POS 401 could take the admin
        // branch, wipe ACTIVE_STORE and navigate to /login.
        //
        // The request itself is unambiguous: whichever bearer it carried
        // owns the failure. A POS token's 401 is a POS problem and must
        // never tear down the admin session, or vice versa.
        const sentAuth = req.headers.get('Authorization') ?? '';
        const posToken = storage.getRaw(StorageKey.POS_TOKEN);
        const carriedPosToken =
          !!posToken && sentAuth.includes(posToken);

        const path = window.location.pathname;
        // Same path rule as before (`startsWith('/pos/')`) — unchanged for
        // every admin / tenant / platform route. The only addition is
        // isPosOnlyDevice: a dedicated POS terminal (standalone code present,
        // no admin token) recovers to the PIN screen even if a 401 fires
        // before the URL settles. A dual-use admin browser always has an
        // admin token, so isPosOnlyDevice is false and behaviour is identical.
        const onPosPath = path.startsWith('/pos/');
        const isPosOnlyDevice = !!standaloneCode && !storage.getRaw(StorageKey.TOKEN);
        // `carriedPosToken` is the reliable signal; the other two remain as
        // fallbacks for requests that carried no bearer at all.
        const isPosContext = carriedPosToken || onPosPath || isPosOnlyDevice;

        if (isPosContext) {
          // ── POS Session Expired ──
          console.log('[ErrorInterceptor] Clearing POS session, returning to POS PIN login');

          // Clear the POS session tokens so the PIN screen starts clean.
          storage.remove(StorageKey.POS_AUTH);
          storage.remove(StorageKey.POS_TOKEN);
          // STUCK-PIN — and the in-memory session. Removing only the stored
          // keys left PosStateService "signed in": the PIN screen returned
          // early to /pos/terminal, posGuard bounced it straight back, and the
          // reused PIN screen sat on "No store selected". clearPosState()
          // keeps POS_STANDALONE_CODE, as required below.
          try {
            injector.get(PosStateService, null)?.clearPosState();
          } catch {
            // Storage is already clean; PosStateService also drops a session
            // whose token is gone from storage the next time it is read.
          }

          // IMPORTANT: never delete POS_STANDALONE_CODE here. It identifies
          // which store's PIN screen to return to; wiping it on a transient
          // 401 is what previously let the guard chain fall through to the
          // admin /login. It is only cleared on an explicit operator exit
          // (PosStateService.exitPos / clearPosState).
          if (standaloneCode) {
            // Standalone / direct-link mode: back to this store's PIN screen.
            setTimeout(() => router.navigate(['/pos/s', standaloneCode]), 100);
          } else {
            // Admin-launched POS: back to the POS entry (empty route → PIN).
            setTimeout(() => router.navigate(['/pos']), 100);
          }
        } else {
          // ── Admin Session Expired ──
          console.log('[ErrorInterceptor] Clearing admin session');
          
          // Clear admin session tokens
          storage.remove(StorageKey.AUTH);
          storage.remove(StorageKey.TOKEN);
          storage.remove(StorageKey.ACTIVE_STORE);
          
          // Redirect to login screen
          setTimeout(() => router.navigate(['/login']), 100);
        }
      }
      
      // Re-throw error for component-level error handling
      // Components can still display user-friendly error messages
      return throwError(() => error);
    })
  );
};
